'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { RoundRecord } from '@/types/game';
import {
  formatPrice,
  getRankKey,
  getTier,
  TIER_BG,
  TIER_EMOJI,
  TIER_TEXT,
} from '@/lib/game-logic';
import { useTranslation } from '@/lib/i18n/LanguageContext';
import { useCountUp } from '@/lib/useCountUp';
import { Logo } from '@/components/Logo';

interface FinalScreenProps {
  history: RoundRecord[];
  maxScore: number;
  onRestart: () => void;
}

export function FinalScreen({ history, maxScore, onRestart }: FinalScreenProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const score = history.reduce((sum, r) => sum + r.result.pointsEarned, 0);
  const shownScore = useCountUp(score, 1400);
  const rank = t(getRankKey(score / maxScore));

  const handleShare = async () => {
    const grid = history.map((r) => TIER_EMOJI[getTier(r.result.percentageError)]).join('');
    const text = `SneakGuessr 👟\n${score}/${maxScore} · ${rank}\n${grid}`;
    const url = window.location.origin;

    if (navigator.share) {
      try {
        await navigator.share({ text, url });
      } catch {
        // user dismissed the share sheet
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable
    }
  };

  return (
    <div className="mx-auto flex h-dvh max-w-3xl flex-col overflow-hidden px-4">
      <header className="shrink-0 py-3">
        <Logo className="text-xl" />
      </header>

      <main className="flex min-h-0 flex-1 flex-col gap-3">
        <section className="card anim-rise shrink-0 px-5 py-5 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted">{t('gameOver')}</p>
          <p className="anim-pop mt-2 font-display text-6xl font-extrabold tabular-nums leading-none text-accent">
            {shownScore}
            <span className="ml-2 font-mono text-sm font-normal text-muted">
              {t('outOf')} {maxScore}
            </span>
          </p>

          <p className="mt-4 text-[10px] uppercase tracking-widest text-muted">{t('yourRank')}</p>
          <p className="font-display text-2xl font-extrabold uppercase tracking-tight">{rank}</p>

          <div className="mx-auto mt-4 flex max-w-sm gap-3">
            <button onClick={onRestart} className="btn-primary flex-1 py-3 text-base">
              {t('playAgain')}
            </button>
            <button onClick={handleShare} className="btn-ghost flex-1 py-3 text-base">
              {copied ? t('copied') : t('share')}
            </button>
          </div>
        </section>

        {/* 10 rounds laid out as 2 columns x 5 rows that stretch to the remaining height */}
        <section
          className="card anim-rise flex min-h-0 flex-1 flex-col overflow-hidden"
          style={{ animationDelay: '120ms' }}
        >
          <h2 className="shrink-0 border-b border-line px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-muted">
            {t('recap')}
          </h2>
          <ul className="grid min-h-0 flex-1 grid-cols-2 grid-rows-5 gap-px bg-line">
            {history.map(({ sneaker, result }, i) => {
              const tier = getTier(result.percentageError);
              return (
                <li key={`${sneaker.id}-${i}`} className="flex min-h-0 items-center gap-2 bg-surface px-2.5 py-1">
                  <div className="stage relative aspect-square h-full max-h-11 flex-shrink-0 overflow-hidden rounded-md">
                    {sneaker.imageUrl && (
                      <Image
                        src={sneaker.imageUrl}
                        alt=""
                        fill
                        sizes="44px"
                        className="object-contain p-0.5 mix-blend-multiply"
                      />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-semibold">{sneaker.name}</p>
                    <p className="truncate font-mono text-[11px] text-muted">
                      {formatPrice(result.guessedPrice)} → <span className="text-fg">{formatPrice(result.actualPrice)}</span>
                    </p>
                  </div>
                  <span className={`hidden font-mono text-xs font-bold tabular-nums sm:block ${TIER_TEXT[tier]}`}>
                    +{result.pointsEarned}
                  </span>
                  <span className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${TIER_BG[tier]}`} aria-hidden />
                </li>
              );
            })}
          </ul>
        </section>
      </main>

      <footer className="shrink-0 py-3 text-center">
        <Link href="/legal" className="text-[11px] text-muted transition-colors hover:text-fg">
          {t('legalNotice')}
        </Link>
      </footer>
    </div>
  );
}
